import { paymentAgreementData, signAgreementPayment } from "./paymentServices";

// Signing an agreement can outlast the load balancer's idle timeout. The
// browser then gets a network error or a 5xx while the server keeps signing,
// and a second submit used to create a duplicate plan. The server now
// rejects concurrent submits with `isProcessing`; in both cases we wait for
// the first request's outcome instead of letting the customer resubmit.

export const SIGNING_POLL_INTERVAL_MS = 5000;
export const SIGNING_WAIT_LIMIT_MS = 3 * 60 * 1000;

export const SIGNING_TIMED_OUT_MSG =
  "This is taking longer than usual. Your agreement is still being processed, and we'll email you once it is complete. Please don't submit it again.";
export const SIGNING_FAILED_MSG =
  "We couldn't complete your agreement. Please check your payment details and try again.";

type SigningResponse = {
  body?: {
    success?: boolean;
    data?: any;
  };
  [key: string]: any;
};

type SigningDeps = {
  post: (payload: any) => Promise<any>;
  getAgreement: (params: any) => Promise<any>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
};

const defaultDeps: SigningDeps = {
  post: signAgreementPayment,
  getAgreement: paymentAgreementData,
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

// No response at all, or a gateway/server error: the request may still be
// running on the server, so its outcome is unknown. 4xx answers and a
// deliberate abort are real outcomes and are passed through unchanged.
export const isUncertainSigningFailure = (error: any): boolean => {
  if (!error || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
    return false;
  }
  const status = Number(error.response?.status || 0);
  return !error.response || status >= 500;
};

const processing = (msg?: string): SigningResponse => ({
  body: { success: false, data: { isProcessing: true, msg } },
});

const failed = (msg: string): SigningResponse => ({
  body: { success: false, data: { msg } },
});

// Every screen reads body.data.msg. Older servers answered an unexpected
// error with body.data as a bare string, which those screens dropped.
export const normalizeSigningResponse = (
  response: SigningResponse
): SigningResponse => {
  const body = response?.body;
  if (!body || body.success || typeof body.data !== "string") return response;
  return { ...response, body: { ...body, data: { msg: body.data } } };
};

export const signAgreementWithRecovery = async (
  payload: any,
  deps: SigningDeps = defaultDeps
): Promise<SigningResponse> => {
  let response: SigningResponse;
  try {
    response = normalizeSigningResponse(await deps.post(payload));
  } catch (error) {
    if (!isUncertainSigningFailure(error)) throw error;
    response = processing();
  }

  if (!response?.body?.data?.isProcessing) return response;

  const slug = payload?.slug;
  const deadline = deps.now() + SIGNING_WAIT_LIMIT_MS;
  while (deps.now() < deadline) {
    await deps.sleep(SIGNING_POLL_INTERVAL_MS);

    let details: any;
    try {
      const agreement = await deps.getAgreement({ slug });
      details = agreement?.body?.data?.getAgreementDetailsData;
    } catch {
      continue;
    }
    if (!details) continue;

    const status = String(details.agreement_status || "").toLowerCase();
    if (["accepted", "completed"].includes(status)) {
      // The plan is signed, so the server returns the saved result (amount,
      // schedule, receipt emails) without saving the card or charging again.
      try {
        const saved = await deps.post(payload);
        if (saved?.body?.success) return saved;
      } catch {
        // fall through to keep waiting for a readable result
      }
      continue;
    }

    // locked_plan stays "P:..." while a signing request is running. Once it
    // is gone and the plan isn't accepted, that request ended without
    // signing (declined card, gateway error) and the customer may retry.
    if (!String(details.locked_plan || "").startsWith("P:")) {
      return failed(SIGNING_FAILED_MSG);
    }
  }

  return processing(SIGNING_TIMED_OUT_MSG);
};
